import React, { useState, useEffect, useMemo } from 'react';
import { Check, Info, Play } from 'lucide-react';
import Sidebar from '../Sidebar';
import { Helmet } from 'react-helmet';
import { badgeTiers, shuffleArray, tabConfigs, educationContent } from './BullflowAcademyConfig';

// LocalStorage keys
const STORAGE_KEYS = {
  completed: 'bf_completed',
  started: 'bf_started',
  quizState: 'bf_quizState'
};

// Reusable button component for tutorials
function VideoButton({ href, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center px-4 py-[6px] rounded-[90px] cursor-pointer bg-[#24252b] hover:bg-[#292a30] transition-colors transition-shadow duration-300 ease-in-out shadow-[0px_2px_3px_rgba(0,0,0,0.3)] hover:shadow-[0px_4px_6px_rgba(0,0,0,0.5)] text-sm"
    >
      <Play className="mr-1.5" size={14} />
      <span>{label}</span>
    </a>
  );
}


export default function BullflowTrainingWithProgress() {
  /* ---------- state ---------- */
  const [completed, setCompleted] = useState(() => JSON.parse(localStorage.getItem(STORAGE_KEYS.completed) || '{}'));
  const [startedMap, setStartedMap] = useState(() => JSON.parse(localStorage.getItem(STORAGE_KEYS.started) || '{}'));
  const [quizState, setQuizState] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEYS.quizState) : null;
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return {};
      }
    }
    return tabConfigs.reduce(
      (acc, tab) => ({
        ...acc,
        [tab.id]: { currentIndex: 0, selected: '', showExplanation: false, score: 0 }
      }),
      {}
    );
  });
  const [activeTab, setActiveTab] = useState(tabConfigs[0].id);

  /* ---------- persistence ---------- */
  useEffect(() => localStorage.setItem(STORAGE_KEYS.completed, JSON.stringify(completed)), [completed]);
  useEffect(() => localStorage.setItem(STORAGE_KEYS.started, JSON.stringify(startedMap)), [startedMap]);
  useEffect(() => localStorage.setItem(STORAGE_KEYS.quizState, JSON.stringify(quizState)), [quizState]);

  /* ---------- derived data ---------- */
  const totalQuestions = tabConfigs.reduce((sum, t) => sum + t.questions.length, 0);
  const totalCorrect = Object.values(quizState).reduce((sum, s) => sum + (s.score || 0), 0);
  const ratio = totalCorrect / totalQuestions;
  const badgeIndex = Math.min(badgeTiers.length - 1, Math.floor(ratio * badgeTiers.length));
  const BadgeInfo = badgeTiers[badgeIndex];

  const currentConfig = tabConfigs.find(t => t.id === activeTab);
  const { questions, label, subtitle } = currentConfig;
  const { currentIndex, selected, showExplanation, score } = quizState[activeTab];
  const total = questions.length;
  const question = questions[currentIndex];

  const completedTabs = Object.values(completed).filter(Boolean).length;
  const overallProgress = completedTabs / tabConfigs.length;

  /* ---------- handlers ---------- */
  const handleTabChange = id => setActiveTab(id);
  const resetTab = id => {
    setQuizState(s => ({ ...s, [id]: { currentIndex: 0, selected: '', showExplanation: false, score: 0 } }));
    setCompleted(c => ({ ...c, [id]: false }));
    setStartedMap(m => ({ ...m, [id]: false }));
  };
  const handleStart = () => {
    resetTab(activeTab);
    setStartedMap(m => ({ ...m, [activeTab]: true }));
  };
  const handleSelect = opt => setQuizState(s => ({ ...s, [activeTab]: { ...s[activeTab], selected: opt } }));
  const handleAnswer = () => {
    if (!selected) return;
    setQuizState(s => {
      const tab = s[activeTab];
      const correct = selected === question.answer;
      return {
        ...s,
        [activeTab]: {
          ...tab,
          showExplanation: true,
          score: correct ? tab.score + 1 : tab.score
        }
      };
    });
  };
  const handleNext = () => {
    setQuizState(s => {
      const tab = s[activeTab];
      const next = tab.currentIndex + 1;
      const done = next >= total;
      if (done) setCompleted(c => ({ ...c, [activeTab]: true }));
      return {
        ...s,
        [activeTab]: {
          ...tab,
          currentIndex: done ? tab.currentIndex : next,
          selected: '',
          showExplanation: false
        }
      };
    });
  };

  /* ---------- UI pieces ---------- */
  const renderTabs = () => (
    <div className="flex space-x-4 border-b border-gray-700 mb-4 items-center">
      {tabConfigs.map(tab => (
        <button
          key={tab.id}
          onClick={() => handleTabChange(tab.id)}
          className={`relative py-2 px-4 transition-transform duration-200 ${activeTab === tab.id ? 'text-white border-b-2 border-white scale-110' : 'text-gray-400 hover:scale-105'
            }`}
        >
          {tab.label}
          {completed[tab.id] ? (
            <Check className="inline-block ml-1 text-green-400" size={16} />
          ) : (
            <Info className="inline-block ml-1 text-blue-400" size={16} />
          )}
        </button>
      ))}
    </div>
  );

  const renderStart = () => (
    <div className="bg-black p-6 rounded-lg w-full">
      <h2 className="text-2xl font-bold mb-2">{label}</h2>
      <p className="mb-6 text-lg">{subtitle}</p>
      <button onClick={handleStart} className="px-6 py-3 bg-green-600 rounded font-semibold mb-4">
        Start Quiz
      </button>
      <p className="text-xs text-gray-400">Educational purposes only. Retry anytime!</p>
    </div>
  );

  const shuffledOptions = useMemo(() => shuffleArray(question.options), [activeTab, currentIndex]);

  const renderQuiz = () => (
    <div className="bg-black p-6 rounded-lg w-full">
      <h2 className="text-xl font-semibold mb-2">
        Question {currentIndex + 1} of {total}
      </h2>
      <p className="mb-6 text-lg">{question.question}</p>
      <div className="space-y-3 mb-6">
        {shuffledOptions.map(opt => (
          <button
            key={opt}
            onClick={() => handleSelect(opt)}
            className={`w-full text-left p-3 border rounded ${selected === opt ? 'border-green-400 bg-green-500 bg-opacity-20' : 'border-gray-700'
              }`}
          >
            {opt}
          </button>
        ))}
      </div>
      <button
        onClick={showExplanation ? handleNext : handleAnswer}
        disabled={!selected}
        className="w-full py-2 bg-green-600 rounded font-semibold disabled:opacity-50 mb-4"
      >
        {showExplanation ? (currentIndex < total - 1 ? 'Next' : 'Finish') : 'Answer'}
      </button>
      {showExplanation && (
        <div className="mt-4 p-4 bg-gray-800 rounded">
          <div className="mb-2">
            {selected === question.answer ? (
              <span className="text-green-400">Correct!</span>
            ) : (
              <span className="text-red-400">Incorrect.</span>
            )}
          </div>
          <div>{question.explanation}</div>
        </div>
      )}
    </div>
  );

  const renderEnd = () => (
    <div className="bg-black p-6 rounded-lg w-full text-center">
      <h2 className="text-3xl font-bold mb-4">🎉 Well Done!</h2>
      <p className="text-xl mb-4">You've completed the “{label}” quiz.</p>
      <p className="mb-6">Your score: {score} of {total}</p>
      <button onClick={handleStart} className="px-6 py-3 bg-blue-600 rounded font-semibold">
        Retake Quiz
      </button>
    </div>
  );
  return (
    <div
      style={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'row',
        background: '#1a1b20'
      }}
    >
      <Sidebar activeItem="academy" />

      <Helmet>
        <title>Bullflow Academy</title>
        <meta name="description" content="Bullflow Academy training quizzes" />
      </Helmet>

      <div style={{ flex: 1, height: '100vh', overflowY: 'auto' }}>
        <div className="min-h-screen bg-[#1a1b20] text-white p-6">

          {/* ─── title + badge + progress (responsive) ─── */}
          <div className="mb-4 flex flex-col md:flex-row md:items-start md:justify-between space-y-4 md:space-y-0">

            {/* page title + badge (added pt-3) */}
            <div className="flex items-center space-x-3 shrink-0 pt-3">
              <h1 className="text-2xl font-bold">Bullflow Academy</h1>
              <div
                className={`flex items-center space-x-1 px-2 py-1 text-sm rounded font-semibold ${BadgeInfo.color}`}
              >
                <BadgeInfo.icon size={14} />
                <span>{BadgeInfo.label}</span>
              </div>
            </div>

            {/* progress card */}
            <div className="w-full md:w-[28rem]">
              <div className="rounded-lg p-3 bg-[#1a1b20]/80 backdrop-blur-sm w-full shadow-md">
                <p className="text-sm text-gray-300 mb-2 md:text-right">
                  Complete all 3 quizzes to earn 20% off your next subscription!
                </p>
                <div className="w-full h-3 bg-gray-700 rounded-md overflow-hidden">
                  <div
                    className="h-full bg-green-500 bg-opacity-60 rounded-md transition-all duration-300"
                    style={{ width: `${Math.round(overallProgress * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* divider */}
          <hr className="border-t border-gray-600 opacity-40 mb-6" />

          {/* education videos */}
          <div className="mb-6 flex flex-wrap gap-4">
            {educationContent.map(video => (
              <VideoButton key={video.href} href={video.href} label={video.label} />
            ))}
          </div>

          {/* tabs & quiz */}
          {renderTabs()}
          <div className="mt-6 space-y-6">
            {!startedMap[activeTab]
              ? renderStart()
              : completed[activeTab]
                ? renderEnd()
                : renderQuiz()}
          </div>
        </div>
      </div>
    </div>
  );


}
